import type { useGT } from 'gt-react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { effectiveQuantity } from './lineQuantity';

type WeightLine = Pick<
  AssignmentLine,
  'unitWeightLbs' | 'topOption' | 'neededQuantity' | 'calculatedQuantity'
>;

/** Weight of the line at its planned quantity, or null when the item has none. */
export function lineWeightLbs(line: WeightLine): number | null {
  if (line.unitWeightLbs == null) return null;
  return line.unitWeightLbs * effectiveQuantity(line);
}

/**
 * Pounds, compact: two decimals for light items (fuses, lugs) so they do not
 * all read "0 lb", whole pounds once the decimals stop mattering.
 */
export function formatLbs(lbs: number): string {
  const digits = lbs < 10 ? 2 : lbs < 100 ? 1 : 0;
  return `${lbs.toLocaleString(undefined, { maximumFractionDigits: digits })} lb`;
}

/** Total weight of quantity-bearing lines, over those with a weight; null if none has. */
export function sumWeightLbs(
  lines: readonly { unitWeightLbs: number | null; quantity: number }[],
): number | null {
  let total: number | null = null;
  for (const l of lines) {
    if (l.unitWeightLbs == null) continue;
    total = (total ?? 0) + l.unitWeightLbs * l.quantity;
  }
  return total;
}

/**
 * The pieces of a quantity's weight hover: per-unit and line weight, formatted.
 * Null when the item has no weight, so the quantity carries no hover at all.
 */
export function weightHint(
  unitWeightLbs: number | null,
  quantity: number,
): { each: string; line: string } | null {
  if (unitWeightLbs == null) return null;
  return {
    each: formatLbs(unitWeightLbs),
    line: formatLbs(unitWeightLbs * quantity),
  };
}

/**
 * A quantity's hover text: "2.13 lb each · 31.9 lb for this line". Weight rides
 * on the quantity rather than taking a column: a reference for freight calls,
 * not part of the buying decision. Undefined (no hover) without a weight.
 */
export function weightHoverTitle(
  unitWeightLbs: number | null,
  quantity: number,
  gt: ReturnType<typeof useGT>,
): string | undefined {
  const hint = weightHint(unitWeightLbs, quantity);
  if (!hint) return undefined;
  return gt('{each} each · {lineWeight} for this line', {
    context: 'line quantity hover, item weight per unit and for the line',
    each: hint.each,
    lineWeight: hint.line,
  });
}
